<script setup lang="ts">
import { computed } from 'vue';

import { cnMerge } from '@/utils/utils.js';

import { buttonVariants } from './button-variants';
import type { KaButtonProps } from './types';
export type { KaButtonProps } from './types';

defineOptions({ name: 'KaButton' });

const props = withDefaults(defineProps<KaButtonProps>(), {
  type: 'button',
  variant: 'default',
  danger: false,
  size: 'base',
  loading: false,
  useFocusVisible: true,
  class: '',
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const isSmall = computed(() => props.size === 'small');
const isLoading = computed(() => props.loading === true);
const isIcon = computed(() => props.icon !== undefined);

function onClick(e: MouseEvent) {
  if (isLoading.value) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  emit('click', e);
}
</script>

<template>
  <button
    :type="props.type"
    :class="
      cnMerge(
        buttonVariants({
          variant: props.variant,
          size: props.size,
          isDanger: props.danger,
          isIcon: props.icon !== undefined,
          isLoading: props.loading,
          useFocusVisible: props.useFocusVisible,
        }),
        props.class,
      )
    "
    :aria-disabled="isLoading || undefined"
    @click="onClick"
  >
    <span
      v-if="$slots.icon || isLoading"
      :class="
        cnMerge(
          'inline-flex items-center text-current [&>svg]:block',
          isSmall ? 'size-4' : 'size-5',
          !isIcon && '-ml-0.5',
          props.icon?.sizeClass,
          props.iconContainerClasses,
        )
      "
    >
      <svg
        v-if="isLoading"
        aria-label="Loading..."
        role="status"
        class="animate-spin"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>

      <slot v-else name="icon" />
    </span>
    <span v-if="isIcon" class="sr-only">
      {{ props.icon?.label }}
    </span>
    <slot v-if="!isIcon" />
  </button>
</template>
